import React, { useState } from "react";
import { RefreshCw, Info } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { DataTable } from "../components/DataTable";
import { Badge } from "../components/Badges";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";

export default function Certificates() {
  const { can } = useAuth();
  const canReissue = can("certificates", "edit");
  const [refresh, setRefresh] = useState(0);
  const [reissue, setReissue] = useState(null);
  const [reason, setReason] = useState("");

  const doReissue = async () => {
    if (!reason) return toast.error("A reissue reason is required");
    try { await api.post(`/certificates/${reissue.id}/reissue`, { reason }); toast.success("Reissued — original preserved, change logged"); setReissue(null); setReason(""); setRefresh((x) => x + 1); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "certificate_number", label: "Cert. no.", sortable: true, colSearch: true, mono: true, render: (v) => <span className="t-strong mono">{v}</span> },
    { key: "description", label: "Equipment", sortable: true, colSearch: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "customer_name", label: "Customer", sortable: true, colSearch: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "calibration_date", label: "Cal. date", sortable: true, mono: true, render: (v) => <span className="t-mut">{fmtDate(v)}</span> },
    { key: "calibration_due_date", label: "Due date", sortable: true, mono: true, render: (v) => <span className="t-mut">{fmtDate(v)}</span> },
    { key: "status", label: "Status", sortable: true, render: (v) => v === "reissued" ? <Badge label="Reissued" cls="b-violet" /> : <Badge label="Issued" cls="b-green" /> },
    { key: "reissue_reason", label: "Reissue reason", render: (v) => <span className="t-mut" style={{ fontSize: 12 }}>{v || "—"}</span> },
  ];
  if (canReissue) columns.push({ key: "id", label: "", render: (_, row) => <button className="btn sm ghost" onClick={() => setReissue(row)} data-testid={`cert-reissue-${row.id}`}><RefreshCw size={13} /> Reissue</button> });

  return (
    <>
      <PageHead crumb="Records" title="Certificates"
        sub="Central read-only certificate store — sortable & searchable, with versioned reissue (CRxx-xxxxx-1)." />

      <DataTable endpoint="/certificates" columns={columns} rowKey="id" testid="certificates"
        defaultSort={{ by: "customer_name", dir: "asc" }} searchPlaceholder="Search cert no., equipment, customer…" refreshSignal={refresh} />

      <div className="notice" style={{ marginTop: 18 }}>
        <Info size={16} />
        <span>Issued certificates are locked read-only. A reissue never overwrites the original — it creates <span className="mono">CRxx-xxxxx-2</span> and logs reason, user, date & time.</span>
      </div>

      {reissue && (
        <Modal open onClose={() => setReissue(null)} title="Reissue certificate" subtitle={`Original: ${reissue.certificate_number}`}
          footer={<><button className="btn ghost" onClick={() => setReissue(null)}>Cancel</button><button className="btn gold" onClick={doReissue} data-testid="cert-reissue-confirm">Create reissue</button></>}>
          <div className="notice" style={{ marginBottom: 16 }}><Info size={16} /><span>This creates a new versioned certificate (…-1, …-2). The original remains untouched and the action is audited.</span></div>
          <div className="fld"><label>Reason for reissue <span className="req">*</span></label>
            <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer name correction" data-testid="cert-reissue-reason" /></div>
        </Modal>
      )}
    </>
  );
}
